import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, BuildingIcon, FileTextIcon, InfoIcon, Loader2 } from "lucide-react";
import { api } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";
import { LlmExtractionsTable } from "@/components/LlmExtractionsTable";
import { formatAddress } from "@/lib/address";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";
import { useRecordNav } from "@/components/RecordNav";

type Company = components["schemas"]["CompanyAdminResponse"];
type Organisation = components["schemas"]["OrganisationAdminResponse"];

type Section = "info" | "organisations" | "bill-extractions";

const navItems: { id: Section; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "info", label: "Info", icon: InfoIcon },
  { id: "organisations", label: "Organisations", icon: BuildingIcon },
  { id: "bill-extractions", label: "Bill Extractions", icon: FileTextIcon },
];

const sectionIds = new Set<Section>(navItems.map((item) => item.id));

function readSectionFromHash(): Section {
  const hash = window.location.hash.replace(/^#/, "") as Section;
  return sectionIds.has(hash) ? hash : "info";
}

// Admin by-id endpoint: the tenant /companies/{id} 400s without client context, and only the admin
// projection carries ClientId for the link back.
export function CompanyPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [section, setSection] = useState<Section>(readSectionFromHash);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onHashChange() {
      setSection(readSectionFromHash());
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .GET("/admin/companies/{companyId}", { params: { path: { companyId } } })
      .then(({ data, error: apiError }) => {
        if (cancelled) return;
        if (apiError || !data) {
          setError("Company not found.");
          return;
        }
        setCompany(data);
      })
      .catch(() => !cancelled && setError("Company could not be loaded."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Companies have no list page of their own: back goes to the owning client's companies.
  useRecordNav({
    back: company?.clientId
      ? { to: `/clients/${company.clientId}#companies`, label: "Client" }
      : { to: "/clients", label: "Clients" },
    kind: "Company",
    title: company?.name ?? "Company",
    items: navItems,
    active: section,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading company…
      </div>
    );
  }

  // From the route, not the response: the generated type makes every field optional.
  if (error || !company || !companyId) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 size-4" />
        <span>{error ?? "Company not found."}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{company.name}</h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{companyId}</p>
        </div>
        {/* Guarded: clientId is optional in the generated type; unguarded it would link to /clients/undefined. */}
        {company.clientId && (
          <Link to={`/clients/${company.clientId}#companies`} className="text-sm hover:underline">
            Client
          </Link>
        )}
      </div>

      <div className="flex gap-6">

        <div className="min-w-0 flex-1">
          {section === "info" && (
            <Card>
              <CardHeader>
                <CardTitle>{company.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid [&>div]:min-w-0 grid-cols-2 gap-4 text-sm md:grid-cols-3">
                  {/* Labelled head office deliberately: it's the one address on a group bill that is NOT a delivery site. */}
                  <Field label="Head office" value={formatAddress(company)} />
                  <Field label="Contact email" value={company.contactEmail ?? null} />
                  {/* Null means "the client-wide setting applies" - not "no threshold", which would
                      read as off. */}
                  <Field label="Auto-assign threshold" value={formatOverride(company.autoAssignThreshold)} />
                  <Field label="Auto-assign margin" value={formatOverride(company.autoAssignMargin)} />
                  <Field label="Created" value={formatDateTime(company.createdAt)} />
                  <Field label="Updated" value={formatDateTime(company.updatedAt)} />
                </dl>
              </CardContent>
            </Card>
          )}

          {section === "organisations" && (
            <section>
              <h2 className="mb-1 text-lg font-semibold">Organisations</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                The sites in this group - the set a bill received against the company can be routed to.
              </p>
              <CompanyOrganisationsTable companyId={companyId} />
            </section>
          )}

          {section === "bill-extractions" && (
            <section>
              <h2 className="mb-1 text-lg font-semibold">Bill extractions</h2>
              <p className="text-muted-foreground mb-4 text-sm">
                What the LLM has cost on documents that arrived in this company's inbox. A row with no
                organisation is one that has not been routed to a site yet.
              </p>
              <LlmExtractionsTable companyId={companyId} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyOrganisationsTable({ companyId }: { companyId: string }) {
  const pagination = useCursorPagination({ initialLimit: 20 });
  const { reset } = pagination;

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reset();
  }, [companyId, reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchOrganisations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/organisations", {
        params: {
          query: {
            CompanyId: companyId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load organisations.");
      } else {
        setOrganisations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchOrganisations();
    return () => {
      cancelled = true;
    };
  }, [companyId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Address</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {organisations.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                  No organisations in this company.
                </TableCell>
              </TableRow>
            ) : (
              organisations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">
                    <Link to={`/organisations/${org.id}`} className="hover:underline">
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/organisations/${org.id}`}>
                      <Badge
                        variant="secondary"
                        className="font-mono text-xs hover:bg-secondary/80"
                        title={org.id}
                      >
                        {org.id?.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{formatAddress(org)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}

function formatOverride(value: number | string | null | undefined): string | null {
  if (value == null) return null;
  return `${value} (overrides the client setting)`;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5">{value ?? <span className="text-muted-foreground">-</span>}</dd>
    </div>
  );
}
